/**
 * 感知报表的数值格式化。
 *
 * 全表只有两种数值单位，混用会得到静默错误的展示，所以两条口径各给一个函数、
 * 不共用：
 *
 * - ``fmtRatio``  —— 后端的 precision/recall/f1 是 **0~1 小数**，展示需 ×100。
 * - ``fmtPp``     —— 所有 ``delta*`` 字段**已经是百分点（pp）**，只加符号和后缀，
 *                    再乘 100 会放大 100 倍。
 *
 * ``null`` 一律渲染 ``EMPTY`` 而不是 ``0.00%``：第一版只算召回率，fp/precision/f1 恒
 * null；聚合侧「任一 case 缺某计数 → 依赖它的比率整体 null」的传染语义也原样传到
 * 前端。把 null 显示成 0 会把「没算」谎报成「算出来是 0」。
 */

/** 缺失值占位。用 em dash 而不是 ``-``，与 0 / 负号在视觉上区分开。 */
export const EMPTY = '—';

/** 0~1 小数 → 百分比文本。``null`` → ``EMPTY``。 */
export const fmtRatio = (v: number | null | undefined, digits = 2): string =>
  v === null || v === undefined ? EMPTY : `${(v * 100).toFixed(digits)}%`;

/** 已是 pp 的差值 → 带符号的 pp 文本。``null`` → ``EMPTY``。 */
export const fmtPp = (v: number | null | undefined, digits = 2): string => {
  if (v === null || v === undefined) return EMPTY;
  const sign = v > 0 ? '+' : '';
  return `${sign}${v.toFixed(digits)}pp`;
};

/** 计数 → 千分位文本。``null`` → ``EMPTY``（0 仍显示 0）。 */
export const fmtCount = (v: number | null | undefined): string =>
  v === null || v === undefined ? EMPTY : v.toLocaleString('zh-CN');

/** 带符号的计数差值。``null`` → ``EMPTY``。 */
export const fmtCountDelta = (v: number | null | undefined): string => {
  if (v === null || v === undefined) return EMPTY;
  const sign = v > 0 ? '+' : '';
  return `${sign}${v.toLocaleString('zh-CN')}`;
};

/** Δ 的涨跌配色。0 与 null 都用中性色——「无变化」不该染成绿色。 */
export const deltaColor = (v: number | null | undefined): string | undefined => {
  if (v === null || v === undefined || v === 0) return undefined;
  return v > 0 ? '#2f9e44' : '#e03131';
};

/** 计数单位的中文名：box = 目标框（OD），cell = BEV 格子（OCC）；未登记的原样显示。 */
export const unitLabel = (unit: string | null | undefined): string => {
  if (unit === 'box') return '目标框';
  if (unit === 'cell') return 'BEV 格子';
  return unit || EMPTY;
};

/**
 * IoU 口径按单位登记（与后端 ``_finalize_bucket`` 一致）：
 * - ``cell``：IoU = Σtp/(Σtp+Σfp+Σfn)，读 ``iou``；
 * - 其余（``box``）：平均 IoU = Σiou_sum/Σtp，读 ``meanIou``。
 */
export const isCellUnit = (unit: string | null | undefined): boolean => unit === 'cell';
